import React from 'react';
import styled from '@benzinga/themetron';
import { DelayedQuote } from '@benzinga/quotes-manager';
import { getColorClassnameByValue } from '@benzinga/frontend-utils';
import { formatPercentage } from '@benzinga/utils';
import { Ticker, TickerLink } from '@benzinga/ticker-ui';
import { DateTime } from 'luxon';

const staticGlobalIndexes = [
  { name: 'Hong Kong', symbol: 'EWH' },
  { name: 'Japan', symbol: 'EWJ' },
  { name: 'Germany', symbol: 'DAX' },
  { name: 'Europe', symbol: 'VGK' },
];

export interface GlobalIndexesProps {
  globalIndexesData: DelayedQuote[];
}

export const GlobalIndexes: React.FC<GlobalIndexesProps> = ({ globalIndexesData }) => {
  const lastUpdatedAt = globalIndexesData?.[globalIndexesData?.length - 1]?.lastTradeTime;
  const updateTime = lastUpdatedAt
    ? DateTime.fromMillis(lastUpdatedAt).setZone('America/New_York').toFormat('hh:mma ZZZZ')
    : null;
  return (
    <Container className="global-indexes-container">
      <div className="header-container global-indexes">
        <strong className="block font-bold text-black text-base">Global Indexes</strong>
        <span>% Change</span>
      </div>
      <ul className="global-indexes-list">
        {Array.isArray(globalIndexesData) &&
          globalIndexesData.map(globalIndex => {
            const name = staticGlobalIndexes.find(
              staticGlobalIndex => staticGlobalIndex.symbol === globalIndex.symbol,
            )?.name;
            return (
              <li key={globalIndex.symbol}>
                <div className="list-item-info">
                  <Ticker
                    symbol={globalIndex.symbol}
                    targetElement={
                      <TickerLink name={name} symbol={globalIndex.symbol}>
                        <div className={`name ${getColorClassnameByValue(globalIndex.change)}`}>{name}</div>
                      </TickerLink>
                    }
                  />
                  <div className={`value ${getColorClassnameByValue(globalIndex.change)}`}>
                    <span className="change-percent-value">
                      {globalIndex.changePercent ? `${formatPercentage(globalIndex.changePercent)}%` : 'TBA'}
                    </span>
                  </div>
                </div>
              </li>
            );
          })}
      </ul>
      <div className="update-time-container">
        {updateTime && <div className="update-time">Data as of {updateTime}</div>}
      </div>
    </Container>
  );
};

const Container = styled.div`
  &.global-indexes-container {
    li .list-item-info,
    .list-item-info-dropdown {
      .value {
        &.red {
          color: ${({ theme }) => theme.colorPalette.red500};
        }
        &.green {
          color: ${({ theme }) => theme.colorPalette.green500};
        }
      }
    }

    .header-container {
      &.global-indexes {
        margin-top: 10px;
        padding-bottom: 4px;
        display: flex;
        align-items: center;
        justify-content: space-between;

        > span {
          font-size: ${({ theme }) => theme.fontSize.xs};
          font-weight: ${({ theme }) => theme.fontWeight.bold};
        }
      }

      > strong {
        font-weight: ${({ theme }) => theme.fontWeight.bold};
        font-size: ${({ theme }) => theme.fontSize.lg};
        color: ${({ theme }) => theme.colorPalette.black};
        line-height: 1.25rem;
      }
    }

    .header-container.global-indexes {
      border-bottom: 2px solid ${({ theme }) => theme.colorPalette.gray100};
    }

    .global-indexes-list {
      li .list-item-info {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: ${({ theme }) => theme.fontSize.sm};
        font-weight: ${({ theme }) => theme.fontWeight.bold};
        padding: 4px 0;
        border-bottom: 1px solid ${({ theme }) => theme.colorPalette.gray100};

        @media (max-width: 800px) {
          height: 38px;
        }

        .name {
          color: ${({ theme }) => theme.colorPalette.blue800};

          &.red {
            &:before {
              border-top-color: #c00;
            }
          }

          &.green {
            &:before {
              border-top-color: #090;
              transform: rotate(180deg);
            }
          }

          &:before {
            content: '';
            width: 0;
            height: 0;
            border-left: 3.5px solid transparent;
            border-right: 3.5px solid transparent;
            vertical-align: middle;
            border-top: 4px solid #000;
            display: inline-block;
            margin-right: 3px;
          }
        }

        .value {
          margin-left: auto;
          padding-right: 3px;

          .divider {
            margin: 0 4px;
          }
        }
      }
      .type-stock {
        .list-item-info {
          cursor: pointer;
        }
      }
    }

    .list-item-info-dropdown {
      font-size: ${({ theme }) => theme.fontSize.sm};
      > div {
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-bottom: 1px solid ${({ theme }) => theme.colorPalette.gray100};
        padding: 2px 0;
      }
    }

    .update-time-container {
      height: 15px;
    }

    .update-time {
      font-size: ${({ theme }) => theme.fontSize.xs};
      text-align: right;
      width: 100%;
      margin-top: 2px;
    }
  }
`;
